import { FormikContextType } from 'formik';
import React, { ReactElement, useEffect, useState } from 'react';
import { classes } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { ISiteTrial } from '~src/siteManagement/serverApi/types/ISiteTrial';
import { Groups } from '~src/userManagement/groups/Groups';
import { IGroupInfo } from '~src/userManagement/groups/models/IGroupInfo';

import { AgentRow } from './AgentRow';
import { agents, selectServerAgents } from './Agents';
import { styles } from './AgentSectionStyles';
import { getTestId } from './AgentSectionsUtils';
import { Agent, AgentPermissionsSectionProps } from './AgentSectionTypes';

// The group-access allowlisting table shared by the Cloud "AI in Tableau" section
// (AgentPermissionsSection) and the Server GenerativeAIOnPremSection. It lives in its own
// file so the on-prem section can import it without pulling in AgentPermissionsSection,
// which imports from tableau-gai and would form an import cycle.
export function AgentPermissionsTable(props: {
  allowTestRegistration: boolean;
  formik: FormikContextType<AgentPermissionsSectionProps>;
  isTrialSite: boolean;
  isTryItMode: boolean;
  registered: boolean;
  tableauAITrial?: ISiteTrial;
  isOnline: boolean;
  // On-prem (Server) only: false while the LLM connection or model tests have not validated,
  // true once they have. Undefined on Cloud, where there is no LLM connection gating and the
  // row applies no extra gating.
  connectionValid?: boolean;
  // On-prem (Server) only: intercept "turn on" so the section can require model selection first
  // (open the Select Models dialog when no models are selected; enable only on Apply). Return true
  // if the enable was handled. Undefined on Cloud, where enabling is always immediate.
  onRequestEnable?: (agent: Agent) => boolean;
  // On-prem (Server) only: returns the reason a feature can't be turned on, or null when it can.
  // When set, the row displays it as the feature's status and disables "turn on". Undefined on
  // Cloud, where no feature is gated this way.
  enableForceDisabledReason?: (agent: Agent) => string | null;
}): ReactElement {
  const { allowTestRegistration, formik, isTrialSite, isTryItMode, registered, tableauAITrial, isOnline, connectionValid, onRequestEnable, enableForceDisabledReason } = props;
  const visibleAgents = selectServerAgents(agents, isOnline);
  const [groups, setGroups] = useState<IGroupInfo[]>([]);
  const [allUsersGroup, setAllUsersGroup] = useState<IGroupInfo>();

  useEffect(() => {
    const getAllGroups = async () => {
      const groups = (await getGroups()).sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));
      const allUsersGroup = groups.find((g) => g.isAllUsersGroup);
      setAllUsersGroup(allUsersGroup);
      setGroups([allUsersGroup].concat(groups.filter((a) => !a.isAllUsersGroup)));
    };
    getAllGroups();

    // Cleanup function
    return () => {
      setGroups([]);
      setAllUsersGroup(undefined);
    };
  }, []); // Empty dependency array is correct here since we only want this to run once on mount

  return (
    <div className={styles.agentSettingsTable} data-testid={getTestId('AgentPermissionsTable')}>
      <div>
        <div className={classes(styles.header, styles.row)}>
          <div className={classes(styles.cell, styles.agentNameCell)}>{Messages.settings_agents_feature_areas()}</div>
          <div className={classes(styles.cell, styles.status)}>{Messages.settings_agents_feature_status()}</div>
          <div className={classes(styles.cell, styles.group)}>{Messages.settings_agents_grant_access_to()}</div>
          <div className={classes(styles.cell, styles.actions)}>{Messages.actions()}</div>
        </div>
      </div>
      <div>
        {visibleAgents.map((agent) => (
          <AgentRow
            agent={agent}
            key={agent.id}
            registered={registered}
            formik={formik}
            groups={groups}
            allUsersGroup={allUsersGroup}
            isTrialSite={isTrialSite}
            allowTestRegistration={allowTestRegistration}
            isTryItMode={isTryItMode}
            tableauAITrial={tableauAITrial}
            connectionValid={connectionValid}
            onRequestEnable={onRequestEnable}
            enableForceDisabledReason={enableForceDisabledReason}
          />
        ))}
      </div>
    </div>
  );
}

async function getGroups(): Promise<IGroupInfo[]> {
  let allGroups: IGroupInfo[] = [];
  let moreItems = true;
  let startIndex = 0;
  const GET_GROUPS_PAGE_SIZE = 600;

  while (moreItems) {
    const pagedGroups = await Groups.all().getSlice(startIndex, GET_GROUPS_PAGE_SIZE);
    allGroups = allGroups.concat(pagedGroups.items);
    startIndex = startIndex + GET_GROUPS_PAGE_SIZE;
    moreItems = startIndex < pagedGroups.total;
  }
  return allGroups;
}
